import ability from '@/libs/acl/ability'
import {SLOT_TYPES} from '@/config/layoutConfig'
import {buildUnitCamerasGridItems} from '@/utils/gridUtils'

class VideoViewService {
    constructor(store) {
        this.store = store
    }

    async setUnitLiveVideo(data) {
        const dvr = data.dvr || data.data.dvr
        if (dvr) {
            return this.setDvrCamerasView(data, dvr)
        }

        const slots = this.createLiveVideoSlots(data)
        const resolveName = this.resolveUnitName(data)
        const tabName = `Live Video ${resolveName}`

        const tab = await this.store.dispatch("grid/addNewTab", {
            tabName,
            layout: 'unit-live',
            slots
        })

        if (tab.status) {
            await this.store.dispatch("grid/setActiveTab", tab.id)
            this.store.commit("grid/forceLayoutSlots", slots)
        }
    }

    async setDvrCamerasView(data, dvr) {
        const cameras = data.data.cameras || data.cameras
        if (!ability.can('live_video', 'Video') || !Array.isArray(cameras)) {
            return
        }

        const camerasByChannel = new Map()
        cameras.forEach(camera => {
            const channel = Number(camera.channel)
            if (camera.channel != null && camera.channel !== '' && Number.isInteger(channel) && channel >= 0 && channel < 64) {
                camerasByChannel.set(channel, camera)
            }
        })
        if (camerasByChannel.size === 0) return

        const tabCount = Math.floor(Math.max(...camerasByChannel.keys()) / 16) + 1
        let firstTabId = null

        for (let page = 0; page < tabCount; page++) {
            const firstChannel = page * 16
            const tabName = `Cámaras 4x4 ${dvr.name} (${dvr.id}) · ${firstChannel + 1}–${firstChannel + 16}`
            const existingTab = Object.entries(this.store.state.grid.tabs).find(([, tab]) => tab.name === tabName)
            if (existingTab) {
                if (!firstTabId) firstTabId = existingTab[0]
                continue
            }

            // Keep empty channels in their fixed positions within the 4x4 grid.
            const slots = buildUnitCamerasGridItems(16).map((slot, index) => {
                const camera = camerasByChannel.get(firstChannel + index)
                return camera ? {
                    ...slot,
                    name: camera.name,
                    data: { camera, region: data.data.region ?? data.data.data?.region ?? '' },
                    type: SLOT_TYPES.CAM_LIVE,
                } : slot
            })

            const tab = await this.store.dispatch("grid/addNewTab", {
                tabName,
                layout: 'unit-cameras',
                slots
            })
            if (!tab.status) break

            await this.store.dispatch("grid/setActiveTab", tab.id)
            this.store.commit("grid/forceLayoutSlots", slots)
            if (!firstTabId) firstTabId = tab.id
        }

        if (firstTabId) {
            await this.store.dispatch("grid/setActiveTab", firstTabId)
        }
    }

    async setUnitCamerasView(data) {
        const cameras = data.data.cameras ? data.data.cameras : data.cameras
        const resolveName = this.resolveUnitName(data)
        const tabName = `Cámaras ${resolveName}`

        if (!ability.can('live_video', 'Video') || !Array.isArray(cameras) || cameras.length === 0) {
            return
        }

        // Mismos slots que al arrastrar una cámara del árbol al grid,
        const gridConfig = buildUnitCamerasGridItems(cameras.length)
        const slots = cameras.slice(0, gridConfig.length).map((camera, index) => {
            const gridItem = gridConfig.find(item => item.pos === index + 1) || gridConfig[index]
            return {
                name: camera.name,
                pos: index + 1,
                data: {
                    camera,
                    region: data.data.region ?? '',
                },
                type: SLOT_TYPES.CAM_LIVE,
                id: gridItem.id,
            }
        })

        const tab = await this.store.dispatch("grid/addNewTab", {
            tabName,
            layout: 'unit-cameras',
            slots
        })

        if (tab.status) {
            await this.store.dispatch("grid/setActiveTab", tab.id)
            this.store.commit("grid/forceLayoutSlots", slots)
        }
    }

    async setUnitRecordingVideo(data) {
        const slots = this.createRecordingVideoSlots(data)
        const resolveName = this.resolveUnitName(data)
        const tabName = `Recording Video ${resolveName}`

        const tab = await this.store.dispatch("grid/addNewTab", {
            tabName,
            layout: 'unit-recording',
            slots
        })

        if (tab.status) {
            await this.store.dispatch("grid/setActiveTab", tab.id)
            this.store.commit("grid/forceLayoutSlots", slots)
        }
    }

    createLiveVideoSlots(data) {
        return this.createVideoSlots(data, SLOT_TYPES.CAM_LIVE, 'live_video')
    }

    createRecordingVideoSlots(data) {
        return this.createVideoSlots(data, SLOT_TYPES.CAM_RECORDED, 'record_video')
    }

    createVideoSlots(data, slotType, permission) {
        const slots = []
        const cameras = data.data.cameras ? data.data.cameras : data.cameras
        const cameraCount = Math.min(cameras.length, 6)
        const layoutType = cameraCount > 4 ? 'liveVideo5x1Layout' : 'liveVideoLayout'
        const gridConfig = this.getGridConfig(layoutType)

        if (ability.can(permission, 'Video')) {
            for (let index = 0; index < cameraCount; index++) {
                const gridItem = gridConfig.find(item => item.pos === index + 1) || gridConfig[index]
                slots.push({
                    name: cameras[index].name,
                    pos: index + 1,
                    data: {
                        camera: cameras[index],
                        region: '',
                    },
                    type: slotType,
                    id: gridItem.id,
                })
            }
        }

        return slots
    }

    getGridConfig(layoutType) {
        return layoutType === 'liveVideo5x1Layout'
            ? require('@/config/liveVideo5x1Layout').default.grid
            : require('@/config/liveVideoLayout').default.grid
    }

    resolveUnitName(data) {
        return data.data.description ? data.data.description : data.data.data.description
    }
}

export default VideoViewService
